import React, { createContext, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';

import { useCheckoutSession, CheckoutSession } from 'hooks/useCheckoutSession';
import { getCartId, getOrCreateCartId } from 'utils/cartId';
import useSessionStorage from 'hooks/useSessionStorage';
import { DATA_FORM, DATA_LEAD } from 'constants/SessionStorageKeys';

type CheckoutSessionContextValue = {
  remoteSessionData: CheckoutSession | null;
  isSessionLoading: boolean;
};

const CheckoutSessionContext = createContext<CheckoutSessionContextValue>({
  remoteSessionData: null,
  isSessionLoading: false,
});

export function CheckoutSessionProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const routerCartId = router.query.cartId as string | undefined;

  const [storageDataForm, setStorageDataForm] = useSessionStorage(DATA_FORM);
  const [storageDataLead, setStorageDataLead] = useSessionStorage(DATA_LEAD);
  const hasEmail = !!storageDataForm?.email;

  useEffect(() => {
    if (routerCartId && !getCartId()) {
      getOrCreateCartId(routerCartId);
    }
  }, [routerCartId]);

  const { isLoading: isCheckoutLoading, sessionData: remoteSessionData } = useCheckoutSession(
    !hasEmail ? routerCartId : undefined
  );

  // Bloqueia redirect enquanto o router ainda não inicializou — router.query pode
  // estar vazio no primeiro render de páginas sem SSR, fazendo isCheckoutLoading
  // inicializar como false mesmo havendo cartId na URL.
  const isSessionLoading = isCheckoutLoading || (!hasEmail && !router.isReady);

  /** Restaura sessionStorage a partir da sessão Redis após handoff */
  useEffect(() => {
    if (!remoteSessionData) return;

    if (!storageDataForm?.email) {
      setStorageDataForm({
        ...(remoteSessionData.additionalData?.dataForm as object ?? {}),
        name: remoteSessionData.name,
        email: remoteSessionData.email,
        phone: remoteSessionData.phone,
      });
    }

    if (!storageDataLead?.leadId) {
      setStorageDataLead({
        ...(remoteSessionData.additionalData?.dataLead as object ?? {}),
        leadId: remoteSessionData.leadId,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteSessionData]);

  return (
    <CheckoutSessionContext.Provider value={{ remoteSessionData, isSessionLoading }}>
      {children}
    </CheckoutSessionContext.Provider>
  );
}

export function useCheckoutSessionContext(): CheckoutSessionContextValue {
  return useContext(CheckoutSessionContext);
}
